import {
  type EdgeData,
  type EdgeId,
  type EdgeRecord,
  type GraphEdgesSettings,
  type NodeData,
  type NodeGraphType,
  type NodeId,
  type NodeRecord,
  type NodeStyle,
  type Point,
  type ReportSeverity,
  getIntentResultNodeStyle,
  snapToGrid,
} from '@ipfabric/utils';
import { makeCompareEnumerated } from '@ipfabric/utils/fp/makeCompareEnumerated';
import { mergeRecords } from '@ipfabric/utils/fp/mergeRecords';
import { onlyValue } from '@ipfabric/utils/fp/onlyValue';
import { plainSet } from '@ipfabric/utils/fp/plainSet';
import { maybeProp, maybePropOf, prop } from '@ipfabric/utils/fp/prop';
import {
  getCollapsedGroupId,
  isCollapsedEntityId,
} from 'components/Graph/commands/collapsing/helpers/collapsedEntityId';
import { createCollapseGroupEdges } from 'components/Graph/commands/collapsing/helpers/createCollapseGroupEdges';
import { destroyEdgesByNodes } from 'components/Graph/commands/collapsing/helpers/destroyEdgesByNodes';
import { putEdgeToGraphDraft } from 'components/Graph/commands/collapsing/helpers/putEdgeToGraphDraft';
import { removeNodeFromState } from 'components/Graph/commands/helpers/removeNodeFromState';
import { buildNodesEdgesMap } from 'components/Graph/helpers/buildNodesEdgesMap';
import { getEdgeOtherNodeId } from 'components/Graph/helpers/getEdgeOtherNodeId';
import { getPointAverage } from 'components/Graph/helpers/getPointAverage';
import { isEdgeConnectedToNode } from 'components/Graph/helpers/isEdgeConnectedToNode';
import {
  type CollapsedNodeGroupId,
  type GraphData,
  type HydratedCollapsedNodeGroup,
  collapsedNodeGroupIdFromNode,
  isCollapsedNodeId,
  nodesDataFromIds,
} from 'components/Graph/model/GraphData';
import { array, option, ord, readonlyArray, record, set } from 'fp-ts';
import { eqStrict } from 'fp-ts/lib/Eq';
import { flow, pipe } from 'fp-ts/lib/function';
import { type Draft, produce } from 'immer';

/**
 * Node collapsing is a quite complex problem, so there is some clear terminology
 * that aims to be unambiguous with other issues in the application:
 *
 * "Collapsed node" - a node inside a "Collapsed node group", such node is temporarily removed from graph.nodes
 * "Collapsed node group" - a group of "Regular nodes" displayed as a single "Group node"
 * "Group node" - a cloud-icon node representing a "Collapsed node group"
 * "Regular node" - any node in the graph except "Group nodes"
 *
 * "Collapsed edge" - "Regular edge" temporarily removed from graph.edges because its source
 *   and/or target is "Collapsed node"
 * "Collapse group edge" - special edge where one or both ends are connected to a "Group node",
 *   it aggregates edges of the same edgeSettingsId that lead to different "Regular nodes"
 *   in a single "Collapse node group". These edges are added and managed by collapse/uncollapse commands
 *   and technically uses the same format as common grouped edges
 * "Regular edge" - edge between "Regular nodes"
 *
 * "Collapsed entity" - "Collapsed edge" or "Collapsed node"
 *
 * Invariants used:
 * TL;DR: "Collapse group edges" for all touched nodes are rebuilt from scratch on each collapse/uncollapse operation.
 * - "Collapsed node groups" always contain only "Regular edges" leading from or to
 *   nodes inside them ("Collapse group edges" are always excluded)
 * - when collapsing/uncollapsing, remove all "Collapse group edges" leading
 *   from or to uncollapsed or merged "Group nodes" (along with all "Regular edges", of course).
 * - When collapsing (including merging "Collapsed node groups"), all "Collapse group edges"
 *   of newly created group are reconstructed from scratch: this gives more freedom in the way
 *   of creating such edges (grouping similar into one etc).
 */

const isAnyNodeHidden = (nodeIds: Set<NodeId>, graph: GraphData): boolean =>
  pipe(
    nodeIds,
    set.some((nodeId) => graph.hiddenNodes.has(nodeId)),
  );

const getInitialGroupNodePosition = (
  nodeIdsToCollapse: Set<NodeId>,
  nodes: NodeRecord,
): Point =>
  pipe(
    nodeIdsToCollapse,
    plainSet.toArray,
    nodesDataFromIds(nodes),
    readonlyArray.map(prop('position')),
    getPointAverage,
    snapToGrid,
  );

/**
 * Returns a prop to use for the group. If all nodes have the same defined prop, use that.
 * Otherwise, the prop will be null.
 */
const getGroupProp = <TKey extends keyof NodeData>(
  property: TKey,
): ((nodesData: readonly NodeData[]) => NodeData[TKey] | null) =>
  flow(
    readonlyArray.map(prop(property)),
    onlyValue<NodeData[TKey] | null>(eqStrict),
    option.toNullable,
  );

const prepareIdsToCollapse = (
  graph: GraphData,
  nodeIdsToCollapse: Set<NodeId>,
) => {
  const nodesToCollapseGraphType = pipe(
    nodeIdsToCollapse,
    plainSet.toArray,
    // Fixed noUncheckedIndexedAccess NIM-19408
    readonlyArray.map((nodeId) => graph.nodes[nodeId]!.graphType),
    onlyValue<NodeGraphType>(eqStrict),
    option.toNullable,
  );

  if (nodesToCollapseGraphType === null) {
    throw new Error('Mixed node graph type');
  }

  /**
   * Contains no group nodes, but rather node IDs within that group.
   */
  const flatNodeIdsToCollapse: Set<NodeId> = new Set();
  const groupIdsToCollapse: Set<CollapsedNodeGroupId> = new Set();
  const groupIdsByNodeIds: Record<NodeId, CollapsedNodeGroupId> = {};

  for (const nodeId of nodeIdsToCollapse) {
    if (isCollapsedNodeId(graph.collapsedNodeGroups)(nodeId)) {
      groupIdsToCollapse.add(nodeId);

      for (const innerNodeId of record.keys(
        // Fixed noUncheckedIndexedAccess NIM-19408
        graph.collapsedNodeGroups[nodeId]!.nodes,
      )) {
        flatNodeIdsToCollapse.add(innerNodeId);
        groupIdsByNodeIds[innerNodeId] = nodeId;
      }
    } else {
      flatNodeIdsToCollapse.add(nodeId);
    }
  }

  return {
    flatNodeIdsToCollapse,
    groupIdsByNodeIds,
    groupIdsToCollapse,
    nodesToCollapseGraphType,
  };
};

/**
 * Edges to be stored to HydratedCollapsedNodeGroup:
 * - all edges stored in collapsed groups being merged
 * - for regular nodes being merged, take all their edges and expand collapsed ones using expandToRegularEdges
 */
const getEdgesToCollapse = (
  draft: Draft<GraphData>,
  nodeIdsToCollapse: Set<NodeId>,
): EdgeRecord => {
  /**
   * - For a regular edge, just return it wrapped into array
   * - For a collapsed group edge, get regular edges it represents = collapsed edges
   *   inside the collapsed edge group that are connected to currently being collapsed node (originNode)
   */
  const expandToRegularEdgeRecord =
    (originNode: NodeId) =>
    (edgeId: EdgeId): EdgeRecord =>
      isCollapsedEntityId(edgeId)
        ? pipe(
            draft.edges,
            maybeProp(edgeId),
            option.chain(
              flow(
                getEdgeOtherNodeId(originNode),
                collapsedNodeGroupIdFromNode,
                maybePropOf(draft.collapsedNodeGroups),
              ),
            ),
            option.map<HydratedCollapsedNodeGroup, EdgeRecord>(
              flow(
                prop('edges'),
                record.filter(isEdgeConnectedToNode(originNode)),
              ),
            ),
            option.getOrElse<EdgeRecord>(() => ({})),
          )
        : record.singleton(edgeId, draft.edges[edgeId]!);

  const getRegularNodeRegularEdges = (nodeId: NodeId): EdgeRecord =>
    pipe(
      draft.nodesEdgesMap,
      prop(nodeId),
      plainSet.toArray,
      array.map(expandToRegularEdgeRecord(nodeId)),
      mergeRecords,
    );

  const getGroupNodeEdges = (nodeId: CollapsedNodeGroupId): EdgeRecord =>
    draft.collapsedNodeGroups[nodeId]?.edges ?? {};

  const getNodeEdgesToCollapse = (nodeId: NodeId): EdgeRecord =>
    isCollapsedEntityId(nodeId)
      ? getGroupNodeEdges(nodeId)
      : getRegularNodeRegularEdges(nodeId);

  return pipe(
    nodeIdsToCollapse,
    plainSet.toArray,
    array.map<NodeId, EdgeRecord>(getNodeEdgesToCollapse),
    mergeRecords,
  );
};

const createCollapseGroupEdgesForGroup = (
  draft: Draft<GraphData>,
  edgesSettings: GraphEdgesSettings,
  group: HydratedCollapsedNodeGroup,
) => {
  /**
   * Check whether one of ends of the edge lies outside of given group
   */
  const isEdgeToOutside = (edge: EdgeData): boolean =>
    !group.nodes[edge.source] || !group.nodes[edge.target];

  pipe(
    Object.values(group.edges),
    array.filter(isEdgeToOutside),
    createCollapseGroupEdges(draft, edgesSettings),
    array.map(putEdgeToGraphDraft(draft)),
  );
};

const getNodesToCollapse = (
  flatNodeIdsToCollapse: Set<NodeId>,
  groupIdsByNodeIds: Record<NodeId, CollapsedNodeGroupId>,
  graph: GraphData,
): NodeRecord =>
  Array.from(flatNodeIdsToCollapse).reduce<NodeRecord>((nodes, nodeId) => {
    // Because we only keep nodes which are actually displayed in `graph.nodes`, we have to access
    // the corresponding group to retrieve node data.
    nodes[nodeId] =
      (groupIdsByNodeIds[nodeId]
        ? // Fixed noUncheckedIndexedAccess NIM-19408
          graph.collapsedNodeGroups[groupIdsByNodeIds[nodeId]!]?.nodes[nodeId]
        : // Fixed noUncheckedIndexedAccess NIM-19408
          null) ?? graph.nodes[nodeId]!;
    return nodes;
  }, {});

type NodeIntentCheckResult = ReportSeverity | null | undefined;

const enumerateIntentCheckResult = (
  result: NodeIntentCheckResult,
): ReportSeverity | -1 | -2 => {
  switch (result) {
    case undefined:
      return -2;
    case null:
      return -1;
    default:
      return result;
  }
};

const getMaxIntentCheckResult: (
  x: NodeIntentCheckResult,
  y: NodeIntentCheckResult,
) => NodeIntentCheckResult = pipe(
  enumerateIntentCheckResult,
  makeCompareEnumerated,
  ord.fromCompare,
  ord.max,
);

const getHighestIntentResultSeverity = (
  nodesDataToCollapse: readonly NodeData[],
) =>
  nodesDataToCollapse.reduce<NodeIntentCheckResult>(
    (acc, node) => getMaxIntentCheckResult(node.intentCheckResult, acc),
    undefined,
  );

const getCollapsedNodeStyle = (
  highestIntentResultSeverity: NodeIntentCheckResult,
): NodeStyle => {
  if (highestIntentResultSeverity !== undefined) {
    return getIntentResultNodeStyle(highestIntentResultSeverity);
  }
  return record.empty;
};

export const collapseNodes = (
  graph: GraphData,
  edgesSettings: GraphEdgesSettings,
  nodeIdsToCollapse: Set<NodeId>,
  position?: Point,
): GraphData => {
  if (nodeIdsToCollapse.size === 0) {
    return graph;
  }

  if (isAnyNodeHidden(nodeIdsToCollapse, graph)) {
    throw new Error('Hidden nodes cannot be collapsed');
  }

  return produce(graph, (draft) => {
    // `flatNodeIdsToCollapse` never contains group IDs, but rather node IDs inside groups (along with other node IDs).
    const {
      flatNodeIdsToCollapse,
      groupIdsByNodeIds,
      groupIdsToCollapse,
      nodesToCollapseGraphType,
    } = prepareIdsToCollapse(draft, nodeIdsToCollapse);

    const nodesToCollapse = getNodesToCollapse(
      flatNodeIdsToCollapse,
      groupIdsByNodeIds,
      draft,
    );
    const nextGroupId = pipe(
      nodesToCollapse,
      Object.values,
      array.map(prop('sn')),
      getCollapsedGroupId,
    );
    const initialGroupPosition =
      position ?? getInitialGroupNodePosition(nodeIdsToCollapse, draft.nodes);

    const nextGroup: HydratedCollapsedNodeGroup = {
      id: nextGroupId,
      originalPosition: initialGroupPosition,
      nodes: nodesToCollapse,
      // We make sure that a group only ever contains regular edges, not collapse group edges.
      // This is an invariant allowing us to perform easier creation and management of collapse group edges
      // along with recreating regular edges on uncollapse.
      edges: getEdgesToCollapse(draft, nodeIdsToCollapse),
    };

    const nodesDataToCollapse = Object.values(nextGroup.nodes);

    const highestIntentResultSeverity =
      getHighestIntentResultSeverity(nodesDataToCollapse);

    draft.nodes[nextGroupId] = {
      boxId: getGroupProp('boxId')(nodesDataToCollapse),
      extra: getGroupProp('extra')(nodesDataToCollapse),
      graphType: nodesToCollapseGraphType,
      children: [],
      id: nextGroupId,
      intentCheckResult: highestIntentResultSeverity,
      label: `${Object.values(nextGroup.nodes).length}`,
      parentPath: getGroupProp('parentPath')(nodesDataToCollapse),
      position: nextGroup.originalPosition,
      sn: nextGroupId,
      style: getCollapsedNodeStyle(highestIntentResultSeverity),
      type: 'cloud',
    };

    draft.collapsedNodeGroups[nextGroupId] = nextGroup;

    nodeIdsToCollapse.forEach(removeNodeFromState(draft));

    for (const groupId of groupIdsToCollapse) {
      delete draft.collapsedNodeGroups[groupId];
    }

    // destroyEdgesByNodes() has to be called after the group is created and before nodesEdgesMap
    // is updated because it extracts information from which edges were connected to being-collapsed nodes.
    destroyEdgesByNodes(draft, nodeIdsToCollapse);
    createCollapseGroupEdgesForGroup(draft, edgesSettings, nextGroup);

    if (
      Array.from(nodeIdsToCollapse).every((id) => draft.hiddenNodes.has(id))
    ) {
      draft.hiddenNodes.add(nextGroupId);
    }

    for (const id of nodeIdsToCollapse) {
      draft.hiddenNodes.delete(id);
    }

    draft.nodesEdgesMap = buildNodesEdgesMap(draft.nodes, draft.edges);
  });
};
